import createClient from "openapi-fetch";
import createQueryClient from "openapi-react-query";
import { AuthService } from "../services/auth";
import { LocaleService } from "../services/locale";
import { RealmsService } from "../services/realms";
import { type paths } from "../types/generated/api";

// Create the base fetch client with auth interceptors
const fetchClient = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_URL || "/api",
});

// Apply the same interceptors as in api.typed.ts
fetchClient.use({
  onRequest: ({ request }) => {
    const token = AuthService.getStoredToken();

    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }

    // Add realm header if a realm is selected
    const selectedRealmId = RealmsService.getSelectedRealm();
    if (selectedRealmId) {
      request.headers.set("x-treel-realm-id", selectedRealmId.toString());
    }

    // Add locale header if a locale is selected
    const selectedLocale = LocaleService.getSelectedLocale();
    if (selectedLocale) {
      request.headers.set("x-treel-locale", selectedLocale);
    }

    return request;
  },
  onResponse: async ({ response, request }) => {
    if (response.status === 401) {
      // Don't retry refresh endpoint to avoid infinite loop
      const url = new URL(request.url);
      if (url.pathname.endsWith("/auth/refresh")) {
        // Clear auth and redirect to login
        AuthService.clearAuth();
        window.location.href = "/login";
        throw new Error("Session expired. Please log in again.");
      }

      // Try to refresh token for other endpoints
      const refreshResponse = await AuthService.refreshAccessToken();
      if (refreshResponse) {
        // Retry the original request with the new token
        request.headers.set(
          "Authorization",
          `Bearer ${refreshResponse.accessToken}`
        );
        return fetch(request);
      }
    }

    return response;
  },
  onError: ({ error }) => {
    console.error("API Error:", error);
    throw error; // Re-throw to propagate the error
  },
});

// Create and export the openapi-react-query client
export const $api = createQueryClient(fetchClient);

// Also export the fetch client in case we need it directly
export { fetchClient };
